import axios from 'axios';
import UnifiedConfig from '../config/unified';

// Separate axios instance for the Node backend (crm-backend-node). Kept
// distinct from the PHP-backed `api` client (axiosConfig.js) so migrated
// and not-yet-migrated modules can run side by side during the transition -
// see src/api/*.js for the per-resource functions that use this.
const nodeApi = axios.create({
  timeout: 30000
});

nodeApi.interceptors.request.use(async (config) => {
  if (!config.baseURL) {
    config.baseURL = await UnifiedConfig.getNodeApiBaseUrl();
  }
  return config;
});

nodeApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      if (typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// A password change/reset elsewhere revokes every existing session: the
// backend answers 401 SESSION_REVOKED. Drop the dead token and send the user
// to the login page with an explanation (read by Login.js).
export const AUTH_NOTICE_KEY = 'authNotice';
nodeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const current = localStorage.getItem('token');
    const headers = error.config?.headers || {};
    const sent = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
    // Only when the rejected token is still the stored one: a request that was
    // in flight while this tab changed its own password must not log it out.
    if (error.response?.status === 401 && error.response.data?.code === 'SESSION_REVOKED' && current && sent === `Bearer ${current}`) {
      localStorage.removeItem('token');
      try {
        sessionStorage.setItem(AUTH_NOTICE_KEY, 'Your session has ended because your password was changed. Please sign in again.');
      } catch (e) {
        // storage unavailable - redirect anyway
      }
      if (window.location.pathname !== '/login') window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

// Absolute URL for a path served statically by the Node backend (e.g. a
// profile image at "uploads/profiles/xyz.png"). Separate from the PHP-era
// toAbsoluteUrl() in axiosConfig.js since uploaded files now live under
// crm-backend-node/uploads, not crm-backend/uploads.
export const toNodeAbsoluteUrl = async (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const baseUrl = await UnifiedConfig.getNodeBackendUrl();
  const cleanPath = path.replace(/^\//, '');
  return `${baseUrl.replace(/\/$/, '')}/${cleanPath}`;
};

export default nodeApi;
